import { useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import { breakpoints, vars } from '@etg/wings';
import { useProperty, useSearch } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import { css } from '@eti/styles';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import TripNotAvailableErrorModal from '../../../../common/error-modals/TripNotAvailableErrorModal';
import { isDesktopViewport } from '../../../../utils/view';
import { getFlightResultToInsertAdFormAfter } from '../../utils/resultUtils';
import { addAdFormScriptTags } from '../adForm';
import ResultPageDebugTools from '../search-filters/debug/ResultPageDebugTools';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import type { NearbyDatePricesType } from '../../models';
import type { Filters } from '../search-filters/v3/MobileFilters';
import { AIRLINES } from '../../../../constants/searchFiltersConstants';
import { useResultPageContext } from './contexts/ResultPageContext';
import SelfTransferModal from './SelfTransferModal';
import ItineraryDrawerSelfTransferModal from './ItineraryDrawerSelfTransferModal';
import SearchResultsMobile from './SearchResultsMobile';
import SearchResultsDesktop from './SearchResultsDesktop';

const searchResultWrapperStyles = css`
  background-color: ${vars.colors.page.background.primary};
  height: 100%;
`;

type DestinationState = {
  fromDestinationPage?: boolean;
};

export interface SearchResultsProps {
  isNearbyDatesLoading: boolean;
  nearbyDatePrices: NearbyDatePricesType;
  onCloseTripNotAvailableModal: () => void;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
}

const SearchResults = ({
  isNearbyDatesLoading,
  nearbyDatePrices,
  onCloseTripNotAvailableModal,
  searchFormWatchRef,
  setFetchMoreResults,
  setValueRef,
}: SearchResultsProps) => {
  const {
    shouldShowTripIsNotAvailableModal,
    searchData,
    isSelfTransferModalOpen,
    setIsSelfTransferModalOpen,
    isItineraryDrawerSelfTransferModalOpen,
    setIsItineraryDrawerSelfTransferModalOpen,
  } = useResultPageContext();
  const { p } = useProperty();
  const { currentSearchData, setSearchData } = useSearch();
  const flightResultsWrapperRef = useRef(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const shouldShowAdform = p('IbeClient.Adform.Enable');
  const isAirlinesEnabled = p('Result.Filter.Airlines.Enabled');
  const adformResultMobileMid = p('IbeClient.Adform.Result.Mobile.Mid');
  const adformResultDesktopMid = p('IbeClient.Adform.Result.Desktop.Mid');
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const isDebugEnabled = window.spa_variables?.debugEnabled;
  const isMobile = useMediaQuery(`(max-width: ${breakpoints._970})`);
  const history = useHistory<DestinationState>();
  const navigatingFromDestinationPage = history?.location?.state?.fromDestinationPage;
  const selectedFilters = currentSearchData?.filters as Filters;
  const hasSelectedAirlines = selectedFilters?.[AIRLINES]?.length;
  const shouldShowAllAirlines =
    navigatingFromDestinationPage &&
    isAirlinesEnabled &&
    hasSelectedAirlines &&
    searchData?.filteredFlightsCount === 0;

  const onFilter = (filter: any) => {
    setSearchData!({ filters: { ...currentSearchData?.filters, ...filter } });
  };

  if (shouldShowAllAirlines) {
    onFilter({ [AIRLINES]: null });
  }

  useEffect(() => {
    const adFormMid = isDesktopViewport() ? adformResultDesktopMid : adformResultMobileMid;
    const shouldShowAdForm = shouldShowAdform && adFormMid;

    if (shouldShowAdForm && flightResultsWrapperRef.current) {
      const flightResultToInsertAdFormAfter = getFlightResultToInsertAdFormAfter(
        flightResultsWrapperRef.current,
      );

      if (flightResultToInsertAdFormAfter) {
        addAdFormScriptTags(flightResultToInsertAdFormAfter, adFormMid, searchData);
      }
    }
  }, [adformResultDesktopMid, adformResultMobileMid, searchData, shouldShowAdform]);

  return (
    <div className={searchResultWrapperStyles}>
      {isMobile ? (
        <SearchResultsMobile
          isNearbyDatesLoading={isNearbyDatesLoading}
          nearbyDatePrices={nearbyDatePrices}
          onFilter={onFilter}
          searchFormWatchRef={searchFormWatchRef}
          selectedFilters={selectedFilters}
          setFetchMoreResults={setFetchMoreResults}
          setValueRef={setValueRef}
          sidebarRef={sidebarRef}
        />
      ) : (
        <SearchResultsDesktop
          isNearbyDatesLoading={isNearbyDatesLoading}
          nearbyDatePrices={nearbyDatePrices}
          onFilter={onFilter}
          searchFormWatchRef={searchFormWatchRef}
          selectedFilters={selectedFilters}
          setFetchMoreResults={setFetchMoreResults}
          setValueRef={setValueRef}
          sidebarRef={sidebarRef}
        />
      )}

      <TripNotAvailableErrorModal
        isOpen={shouldShowTripIsNotAvailableModal}
        onPrimaryAction={onCloseTripNotAvailableModal}
      />

      {isVISelfTansferDealEnabled && (
        <>
          <SelfTransferModal
            isOpen={isSelfTransferModalOpen}
            setIsOpen={setIsSelfTransferModalOpen}
          />
          <ItineraryDrawerSelfTransferModal
            isOpen={isItineraryDrawerSelfTransferModalOpen}
            setIsOpen={setIsItineraryDrawerSelfTransferModalOpen}
          />
        </>
      )}

      {isDebugEnabled && (
        <ResultPageDebugTools
          availableSortTypes={searchData?.availableSortTypes ?? []}
          // @ts-ignore - WEBTECH-1361
          debugFilters={searchData.availableFilters}
          filteredFlightsCount={searchData?.filteredFlightsCount ?? 0}
          flightsCount={searchData?.flightsCount ?? 0}
          onFilter={(filter: any) =>
            setSearchData!({ filters: { ...currentSearchData?.filters, ...filter } })
          }
          selectedFilters={currentSearchData?.filters}
        />
      )}
    </div>
  );
};

export default SearchResults;
